import {
  DropDownButton,
  type DropDownButtonProps,
} from '@progress/kendo-react-buttons'
import React from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'
import { match } from 'ts-pattern'

import { HStack } from '~/components/elements/HStack'
import {
  IconButton,
  type IconButtonProps,
} from '~/components/elements/IconButton'
import { VStack } from '~/components/elements/VStack'
import { CustomerAttributeGroupSelector } from '~/components/features/CustomerAttributeGroupSelector'
import { QuestionTreeSelector } from '~/components/features/QuestionTreeSelector'
import { useFormField } from '~/hooks/useFormField'
import { useSurveyQueryQuestions } from '~/hooks/useSurveyQuery'
import { compact, ensureExists, findByPath } from '~/utils'

import { useAvailableCustomerAttributeGroups } from '../../hooks/useAvailableCustomerAttributeGroups'

import styles from './FilterInput.css'
import { type InputValues } from './utils'

type Category = InputValues['filters'][number]['category']

type CategorySelectButtonItem = {
  label: string
  value: Category
}

export type FilterInputProps = {
  index: number
  onDelete: (index: number) => void
}

export const FilterInput = ({ index, onDelete }: FilterInputProps) => {
  const { t } = useTranslation()
  const selectFromQuestionnaireResponseLabel = t(
    'select_from_questionnaire_response',
  )
  const selectFromCustomizedSegmentsLabel = t('select_from_customized_segments')
  const { control } = useFormContext<InputValues>()
  const categoryField = useFormField(control, `filters.${index}.category`)
  const idField = useFormField(control, `filters.${index}.id`)

  const questions = useSurveyQueryQuestions()
  const customerAttributeGroups = useAvailableCustomerAttributeGroups(undefined)

  const categorySelectButtonItems = React.useMemo<CategorySelectButtonItem[]>(
    () =>
      compact([
        0 < questions.length && {
          label: selectFromQuestionnaireResponseLabel,
          value: 'Question',
        },
        0 < customerAttributeGroups.length && {
          label: selectFromCustomizedSegmentsLabel,
          value: 'CustomerAttribute',
        },
      ]),
    [
      customerAttributeGroups.length,
      questions.length,
      selectFromCustomizedSegmentsLabel,
      selectFromQuestionnaireResponseLabel,
    ],
  )

  const handleCategorySelectButtonItemClick = React.useCallback<
    EH<DropDownButtonProps, 'onItemClick'>
  >(
    (event) => {
      const category = event.item.value as Category
      categoryField.onChange(category)
      idField.onChange(
        match(category)
          .with('Question', () => R.first(questions)?.question_id)
          .with('CustomerAttribute', () => R.first(customerAttributeGroups)?.id)
          .exhaustive(),
      )
    },
    [categoryField, customerAttributeGroups, idField, questions],
  )

  const handleDeleteButtonClick = React.useCallback<
    EH<IconButtonProps, 'onClick'>
  >(() => {
    onDelete(index)
  }, [index, onDelete])

  return (
    <VStack border="light" padding={2} spacing={2}>
      <HStack justify="space-between" spacing={2}>
        <DropDownButton
          className={styles.categorySelectButton}
          items={categorySelectButtonItems}
          text={R.pipe(
            categorySelectButtonItems,
            findByPath('value', categoryField.value),
            ensureExists,
            R.prop('label'),
          )}
          textField="label"
          onItemClick={handleCategorySelectButtonItemClick}
        />

        <IconButton
          buttonStyle="light"
          fillMode="flat"
          icon="delete"
          onClick={handleDeleteButtonClick}
        />
      </HStack>

      {match(categoryField.value)
        .with('Question', () => (
          <QuestionTreeSelector questions={questions} {...idField} />
        ))
        .with('CustomerAttribute', () => (
          <CustomerAttributeGroupSelector
            customerAttributeGroups={customerAttributeGroups}
            {...idField}
          />
        ))
        .exhaustive()}
    </VStack>
  )
}
